import { useState, useCallback, useRef, useEffect } from "react";
import { debounce } from "@/utils/debounce";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listProductInfos } from "@/graphql/queries";
import { getDefaultParseVariants } from "@/utils/helper/getDefaultParseVariants";
import { sortProductByName } from "@/utils/helper/sortProductByName";
import { Product } from "@/types/AppTypes";

interface UseFilteredProductSearchOptions {
  debounceMs?: number;
}

interface SearchFilters {
  search: string;
  brandId?: string;
  categoryId?: string;
}

export function useFilteredProductSearch({ debounceMs = 400 }: UseFilteredProductSearchOptions = {}) {
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchRef = useRef(
    debounce(async (filters: SearchFilters) => {
      
      try {
        const graphqlFilter: any = {
          is_active: { eq: true },
          is_deleted: { eq: false },
        };

        if (filters.search.trim()) {
          graphqlFilter.name = { 
            contains: filters.search.slice(0, 1).toUpperCase() + filters.search.slice(1) 
          };
        }

        if (filters.brandId && filters.brandId !== "all") {
          graphqlFilter.brand_id = { contains: filters.brandId };
        }

        if (filters.categoryId && filters.categoryId !== "all") {
          graphqlFilter.category_id = { contains: filters.categoryId };
        }

        const productRes = await executeGraphQLMutation({
          query: listProductInfos,
          variables: {
            filter: graphqlFilter,
            limit: 100, 
          },
          showToast: false,
        });

        if (productRes && productRes.items) {
          setResults(sortProductByName(getDefaultParseVariants(productRes.items)));
        } else {
          setResults([]);
        }
      } catch (err: any) {
        setError(err.message || "Error searching products");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs)
  );

  const searchProducts = useCallback(
    (filters: SearchFilters) => {
      if (filters.search.trim() || 
          (filters.brandId && filters.brandId !== "all") || 
          (filters.categoryId && filters.categoryId !== "all")) {
        // Set loading immediately when user starts typing/filtering
        setLoading(true);
        setError(null);
        // Then trigger debounced search
        debouncedSearchRef.current(filters);
      } else {
        setResults([]);
        setLoading(false);
        setError(null);
      }
    },
    []
  );

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    results,
    loading,
    error,
    searchProducts,
    clearResults,
  };
}
